import type { AxiosRequestConfig } from "axios"
import Papa from "papaparse"

import { emdClient } from "@/utils/apiClients"

export type ValueFromCsv = string | number | undefined
export type CsvRecord = Record<string, ValueFromCsv>

export default class BlobApi {
  private getApiClient() {
    if (!emdClient) {
      throw new Error("API client is not initialized")
    }
    return emdClient
  }

  async getBlobData<T>(blobPath: string, config: AxiosRequestConfig = {}) {
    const api = this.getApiClient()
    const response = await api.get(`/api/blobs/${blobPath}`, config)
    if (response.status !== 200) {
      throw new Error(`Failed to fetch blob data: ${response.statusText}`)
    }
    return response.data as T
  }

  async writeBlobData<T>(blobPath: string, data: T) {
    const api = this.getApiClient()
    const response = await api.post(`/api/blobs/${blobPath}`, data)
    if (response.status !== 200) {
      throw new Error(`Failed to write blob data: ${response.statusText}`)
    }
    return response.data
  }

  async getCsvBlobData<T extends CsvRecord>(blobPath: string, parseOptions: Papa.ParseConfig = {}) {
    const api = this.getApiClient()
    const { data, status } = await api.get(`/api/blobs/${blobPath}`, { responseType: "blob" })
    if (status !== 200) return []

    return new Promise<T[]>((resolve, reject) => {
      Papa.parse<T>(data, {
        header: true,
        dynamicTyping: true,
        skipEmptyLines: true,
        complete(result) {
          if (result.errors.length > 0) reject(result.errors)
          else resolve(result.data)
        },
        ...parseOptions
      })
    })
  }

  //struggled to get the type right with typescript when header is false. so make a separate function instead
  async getCsvBlobDataWithoutHeader(blobPath: string, parseOptions: Papa.ParseConfig = {}) {
    const api = this.getApiClient()
    const { data, status } = await api.get(`/api/blobs/${blobPath}`, { responseType: "blob" })
    if (status !== 200) return []
    return new Promise<string[][]>((resolve, reject) => {
      Papa.parse<string[]>(data, {
        header: false,
        dynamicTyping: true,
        skipEmptyLines: true,
        complete(result) {
          if (result.errors.length > 0) reject(result.errors)
          else resolve(result.data)
        },
        ...parseOptions
      })
    })
  }

  async requestLastUpdateInfo(path: string, options: Papa.ParseConfig = {}) {
    const parsed = await this.getCsvBlobDataWithoutHeader(path, { header: false, delimiter: " ", ...options })
    return parsed[0]
  }
}
